import { ref } from '@vue/composition-api'
import { uniqBy, isEmpty } from 'lodash-es'

import { useCurrentUser, useUserCompany, useUserCompanyRLS } from '@composables'

import { nextTick } from '@vue/composition-api'

export const useActiveCashCall = () => {
  const { isSuperUser } = useCurrentUser()
  const { isRoleName: isCompanyRoleName } = useUserCompany()
  const getDefaultCashCall = () => ({
    id: 0,
    claim_loss_established: true,
    claim_reason: '',
    reason_manual: '',
    claim_authority_scope: true,
    bdx_key_exists: true,
    claim_exists: true,
    status: 'DRAFT',
  
    umr: '',
    sn: '',
    rc: '',
    cc: '',
    currency_type: '',
    year_of_account: 0,
    bdx_key: '',
    bdx_key_manual: '',
    claim_ref: '',
    claim_ref_manual: '',
  
    tpa: '',
    broker: '',
    new_broker: [],
    coverholder: '',
    underwriter_lead: '',
  
    insured_name: '',
    date_of_loss: '',
    date_claim_made: '',
    date_risk_effective: '',
    date_risk_expiry: '',
    date_recent_report_period: '',
    loss_country: '',
    loss_state: '',
    loss_sl_state: '',
    canada_province: '',
    sanction_status: '',
    naic_code: '',
    cause_of_loss: '',
  
    fee_exists: false,
    amount: 0.0,
    total_fee: 0.0,
    split_fee: 0.0,
    total_indemnity: 0.0,
    expenses_amount: 0.0,
    attorney_coverage_amount: 0.0,
    adjuster_fee_amount: 0.0,
    defence_fees_amount: 0.0,
    tpa_fees_amount: 0.0,
    initial_loss_fund_value: 0.0,
    current_loss_fund_balance: 0.0,
  
    sl_tax: [],
    date_of_request: '',
    account_id: '',
    upload_doc_type: '',
    supporting_documents: [],
  
    account_name: '',
    account_code: '',
    account_type: '',

    cash_call_comments: '',
    statutory_deadline: '',
  })

  const activeCashCall = ref(getDefaultCashCall())

  const setActiveCashCall = async (item) => {
    const toNumber = (v) => parseFloat(String(v).replace(/,/g, '').replace(/\$/g, '')) || 0
    // Loss_Fund_Exists is a real boolean in the API, while Authority_Scope is a
    // CharField that stores "True"/"False" - accept both so a saved "No" is
    // restored as false instead of being coerced to true (LIQ-1190).
    const toBool = (v, fallback = true) => {
      if (v === null || v === undefined || v === '') return fallback
      if (typeof v === 'boolean') return v
      return !['false', '0', 'no'].includes(String(v).trim().toLowerCase())
    }

    // Force change so watchers always fire
    activeCashCall.value = null
    await nextTick()
    activeCashCall.value = item

    activeCashCall.value.id = item.id || 0
    activeCashCall.value.status = item.Status || 'DRAFT'
    activeCashCall.value.claim_loss_established = toBool(item.Loss_Fund_Exists)
    activeCashCall.value.claim_authority_scope = toBool(item.Authority_Scope)
    activeCashCall.value.claim_reason = item.Cash_Call_Reason || undefined
    activeCashCall.value.bdx_key_exists = item.Bdx_Key_Exists || true
    activeCashCall.value.bdx_key = item.Bdx_Key || undefined
    activeCashCall.value.claim_ref = item.Claim_Reference || undefined

    activeCashCall.value.insured_name = item.Insured_Name || undefined
    activeCashCall.value.date_of_loss = item.Date_of_Loss || undefined
    activeCashCall.value.date_claim_made = item.Claim_Date || undefined
    activeCashCall.value.date_risk_effective = item.Risk_Effective_Date || undefined
    activeCashCall.value.date_risk_expiry = item.Risk_Expiration_Date || undefined
    activeCashCall.value.date_binder_inception = item.Binder_Contract_Inception || undefined
    activeCashCall.value.date_binder_expiry = item.Binder_Contract_Expiry || undefined
    activeCashCall.value.date_recent_report_period = item.Risk_Effective_Date || undefined

    
    activeCashCall.value.loss_country = item.Claim_Country || undefined
    activeCashCall.value.loss_state = item.Claim_State || undefined
    activeCashCall.value.loss_sl_state = item.SL_Tax_State || undefined
    activeCashCall.value.canada_province = item.Claim_Province || undefined

    activeCashCall.value.tpa = item.TPA || undefined
    activeCashCall.value.broker = item.Broker || undefined
    activeCashCall.value.coverholder = item.Coverholder || undefined
    activeCashCall.value.underwriter_lead = item.Lead_Underwriter || undefined

    activeCashCall.value.naic_code = item.NAIC_Code || ''
    activeCashCall.value.cause_of_loss = item.Cause_of_Loss || ''
    activeCashCall.value.sanction_status = item.Sanctions_Check || undefined
    activeCashCall.value.amount = toNumber(item.Cash_Call_Request_Amount) || 0.0
    activeCashCall.value.total_fee = toNumber(item.Total_Fee_Amount) || 0.0
    activeCashCall.value.total_indemnity = toNumber(item.Total_Indemnity_Amount) || 0.0
    activeCashCall.value.expenses_amount = toNumber(item.Expenses_Amount) || 0.0
    activeCashCall.value.attorney_coverage_amount = toNumber(item.Attorney_Coverage_Amount) || 0.0
    activeCashCall.value.adjuster_fee_amount = toNumber(item.Adjusters_Fees_Amount) || 0.0
    activeCashCall.value.defence_fees_amount = toNumber(item.Defence_Fees_Amount) || 0.0
    activeCashCall.value.tpa_fees_amount = toNumber(item.TPA_Fees_Amount) || 0.0
    activeCashCall.value.initial_loss_fund_value = toNumber(item.Initial_Loss_Fund_Value) || 0.0
    activeCashCall.value.current_loss_fund_balance = toNumber(item.Current_Loss_Fund_Balance) || 0.0

    activeCashCall.value.fee_exists = activeCashCall.value.total_fee !== 0.0

    activeCashCall.value.cash_call_comments = item.cash_call_comments || ''

    // Statutory Deadline (Step 4). Normalized here so reopening a draft
    // restores the date instead of showing an empty mm/dd/yyyy field. The API
    // returns it PascalCase (Statutory_Deadline) like the other model fields.
    activeCashCall.value.statutory_deadline = item.Statutory_Deadline || ''


    console.log('activeCashCall is')
    console.log(activeCashCall)
  }

  const resetActiveCashCall = () => {
    activeCashCall.value = getDefaultCashCall()
  }

  return {
    activeCashCall,

    setActiveCashCall,
    resetActiveCashCall
  }
}